<template>
  <div class="container">
    <!-- Tabs -->
    <div class="tabs-bar">
      <div
        class="tab-item"
        :class="{ active: activeTab === 'waiting' }"
        @click="switchTab('waiting')"
      >
        {{ $t("codTransferWaiting.tab_waiting") }}
      </div>
      <div
        class="tab-item"
        :class="{ active: activeTab === 'completed' }"
        @click="switchTab('completed')"
      >
        {{ $t("codTransferWaiting.tab_completed") }}
      </div>
    </div>

    <!-- Header -->
    <div class="page-header">
      <div class="header-title">
        <i class="fal fa-clock" v-if="activeTab === 'waiting'"></i>
        <i class="fal fa-clock" v-else></i>
        <span>
          {{ activeTab === 'waiting' ? $t("codTransferWaiting.waiting_list_title") : $t("codTransferWaiting.completed_list_title") }}
          <span class="count">({{ count }})</span>
        </span>
      </div>
      <div class="header-amount">
        <span class="amount-label">
          {{ activeTab === 'waiting' ? $t("codTransferWaiting.waiting_total_label") : $t("codTransferWaiting.tab_completed") }}
        </span>
        <span class="amount-value">{{ formatCurrency(totalAmount) }} {{ $t("common.ກີບ") }}</span>
      </div>
    </div>

    <!-- Filters -->
    <div class="box">
      <div class="columns">
        <div class="column is-3">
          <label class="label">{{ $t("codTransferWaiting.search") }}:</label>
          <input class="input" type="text" v-model="searchItem" @keyup.enter="filterBySearch" />
        </div>
        <div class="column is-3">
          <label class="label">{{ $t("codTransferWaiting.shop_status") }}:</label>
          <div class="select">
            <select class="input" v-model="shop_status" @change="fetchData">
              <option value="">{{ $t("common.all") }}</option>
              <option value="open">{{ $t("codTransferWaiting.status_open") }}</option>
              <option value="close">{{ $t("codTransferWaiting.status_close") }}</option>
            </select>
          </div>
        </div>
        <div class="column is-2">
          <div class="control filter-btn-wrap">
            <vs-button circle icon flat @click="filterBySearch">
              <i class="fal fa-search"></i>
            </vs-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="center fit-width">
      <vs-table ref="table">
        <template #thead>
          <vs-tr>
            <vs-th>Action</vs-th>
            <vs-th>{{ $t("codTransferWaiting.no") }}</vs-th>
            <vs-th>{{ $t("codTransferWaiting.shop_name") }}</vs-th>
            <vs-th>{{ $t("codTransferWaiting.tel") }}</vs-th>
            <vs-th>{{ $t("codTransferWaiting.bill_count") }}</vs-th>
            <vs-th>{{ $t("codTransferWaiting.cod_bill_type") }}</vs-th>
            <vs-th>{{ $t("codTransferWaiting.withdraw_percent") }}</vs-th>
            <vs-th>{{ $t("codTransferWaiting.fee") }}</vs-th>
            <vs-th>{{ $t("codTransferWaiting.status") }}</vs-th>
          </vs-tr>
        </template>
        <template #tbody>
          <vs-tr
            v-for="(row, index) in $vs.getPage(rows, page, max)"
            :key="row.id"
            :data="row"
          >
            <vs-td>
              <button
                v-if="activeTab === 'waiting'"
                class="btn-confirm"
                @click="openUploadSlip(row)"
              >
                {{ $t("codTransferWaiting.confirm_attach_slip") }}
              </button>
              <button
                v-else
                type="button"
                class="slip-thumb"
                @click="openSlipPreview(row)"
                :title="$t('codTransferWaiting.click_to_view_slip')"
              >
                <img :src="row.slip_image_url" alt="slip" />
                <span class="slip-overlay"><i class="fal fa-search-plus"></i></span>
              </button>
            </vs-td>
            <vs-td>{{ (pagination.current_page - 1) * limit_page + index + 1 }}</vs-td>
            <vs-td>{{ row.name }}</vs-td>
            <vs-td>{{ row.customer_tel }}</vs-td>
            <vs-td>{{ formatCurrency(row.bill_count) }} {{ $t("common.ກີບ") }}</vs-td>
            <vs-td>{{ getCodTypeLabel(row.cod_type_key) }}</vs-td>
            <vs-td>{{ row.withdraw_percent }}%</vs-td>
            <vs-td>{{ row.fee_percent }}%</vs-td>
            <vs-td>
              <span
                class="status-pill"
                :class="row.status === 'completed' ? 'status-success' : 'status-waiting'"
              >
                {{ row.status === 'completed' ? $t("codTransferWaiting.success") : $t("codTransferWaiting.waiting") }}
              </span>
            </vs-td>
          </vs-tr>
        </template>
        <template #footer>
          <CodPagination v-if="pagination.last_page > 1" :pagination="pagination" :offset="offset" @paginate="fetchData" />
          <Loading v-if="isLoading" />
        </template>
      </vs-table>
    </div>

    <!-- Upload Slip modal (uses root modalEdit slot) -->
    <ModalEdit>
      <template v-slot="{ close }">
        <UploadSlipDialog
          v-if="selectedRow"
          :item="selectedRow"
          @close="close"
          @success="onUploadSuccess"
        />
      </template>
    </ModalEdit>

    <!-- Success confirmation (uses root modalNotification slot) -->
    <ModalNotification>
      <template v-slot="{ close }">
        <SuccessConfirmDialog
          v-if="successItem"
          :item="successItem"
          @close="onSuccessClose(close)"
        />
      </template>
    </ModalNotification>

    <!-- Slip preview lightbox (uses root modalPreviewFile slot) -->
    <ModalPreviewFile>
      <template v-slot="{ close }">
        <SlipPreviewDialog
          v-if="previewItem"
          :item="previewItem"
          @close="onPreviewClose(close)"
        />
      </template>
    </ModalPreviewFile>
  </div>
</template>

<script>
import Loading from '@/views/Loading';
import CodPagination from '@/components/CodPagination';
import UploadSlipDialog from '@/views/Corporate/CodTransferWaiting/Dialog/UploadSlip';
import SuccessConfirmDialog from '@/views/Corporate/CodTransferWaiting/Dialog/SuccessConfirm';
import SlipPreviewDialog from '@/views/Corporate/CodTransferWaiting/Dialog/SlipPreview';
import { fetchCodWaitingList, fetchCodCompletedList } from '@/views/Corporate/CodTransferWaiting/api';
import { getCodTypeLabel } from '@/views/Corporate/CodTransferWaiting/mockData';

export default {
  components: { Loading, CodPagination, UploadSlipDialog, SuccessConfirmDialog, SlipPreviewDialog },
  data: () => ({
    activeTab: 'waiting',
    page: 1,
    max: 10,
    isLoading: true,
    shop_status: '',
    searchItem: '',
    rows: [],
    count: 0,
    totalAmount: 0,
    offset: 10,
    limit_page: 10,
    pagination: { current_page: 1, last_page: 1 },
    selectedRow: null,
    successItem: null,
    previewItem: null,
  }),
  watch: {
    searchItem(v) { if (v === '') this.fetchData(); },
  },
  methods: {
    getCodTypeLabel,
    formatCurrency(v) { return (Number(v) || 0).toLocaleString('en-US'); },

    switchTab(tab) {
      if (this.activeTab === tab) return;
      this.activeTab = tab;
      this.pagination.current_page = 1;
      this.fetchData();
    },

    fetchData(options = {}) {
      this.isLoading = true;
      const params = {
        page: options.current_page ?? this.pagination.current_page,
        per_page: options.per_page ?? this.limit_page,
        search: this.searchItem,
      };
      const fn = this.activeTab === 'waiting' ? fetchCodWaitingList : fetchCodCompletedList;
      fn(this.$http, params).then((res) => {
        this.rows = res.data.data;
        this.count = res.data.total;
        this.totalAmount = res.data.total_amount || 0;
        this.pagination = res.data;
        this.isLoading = false;
      }).catch(() => { this.isLoading = false; });
    },

    openUploadSlip(row) {
      this.selectedRow = JSON.parse(JSON.stringify(row));
      this.$store.commit('modalEdit_State', true);
    },

    onUploadSuccess(uploadedRow) {
      this.successItem = uploadedRow || this.selectedRow;
      this.$nextTick(() => {
        this.$store.commit('modalNotification_State', true);
      });
      this.fetchData();
    },

    onSuccessClose(close) {
      this.successItem = null;
      close();
    },

    openSlipPreview(row) {
      this.previewItem = row;
      this.$nextTick(() => {
        this.$store.commit('modalPreviewFile_State', true);
      });
    },

    onPreviewClose(close) {
      this.previewItem = null;
      close();
    },

    filterBySearch() {
      this.pagination.current_page = 1;
      this.fetchData();
    },
  },
  created() {
    this.fetchData();
  },
};
</script>

<style lang="scss" scoped>
.container { margin-top: 24px; }

.tabs-bar {
  display: flex;
  gap: 28px;
  border-bottom: 1px solid #e6e9ef;
  margin-bottom: 18px;
}
.tab-item {
  padding: 10px 4px;
  font-size: 15px;
  color: #6b7080;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  &:hover { color: #195BFF; }
  &.active {
    color: #195BFF;
    font-weight: 600;
    border-bottom-color: #195BFF;
  }
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.header-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 17px;
  font-weight: 500;
  color: $primary-color;
  i { font-size: 18px; }
  .count { margin-left: 4px; color: $primary-color; }
}
.header-amount {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 500;
  color: $primary-color;
}

.btn-confirm {
  background: #195BFF;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  font-family: $font;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: #1450e6; }
}

.slip-thumb {
  position: relative;
  width: 80px;
  height: 56px;
  border-radius: 6px;
  overflow: hidden;
  background: #f5f7fb;
  border: 1px solid #e6e9ef;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.15s;
  font-family: $font;
  img { width: 100%; height: 100%; object-fit: cover; }
  &:hover {
    border-color: #195BFF;
    .slip-overlay { opacity: 1; }
  }
}
.slip-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(25, 91, 255, 0.55);
  color: #fff;
  font-size: 16px;
  opacity: 0;
  transition: opacity 0.15s;
}

.status-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}
.status-waiting { color: #d49a00; background: #fff5d6; }
.status-success { color: #22a06b; background: #e6f5ee; }

.filter-btn-wrap {
  display: flex;
  align-items: flex-end;
  height: 100%;
}

.fit-width { white-space: nowrap; }

::v-deep .vs-table__td,
::v-deep .vs-table__th {
  vertical-align: middle !important;
  white-space: nowrap !important;
  width: 1px !important;
}
</style>
